import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { cssTransition, ToastContainer } from 'react-toastify';
import AppErrorBoundary from '@/components/appErrorBoundary';
import { Suspense, useEffect } from 'react';
import useRoutes from './routes';
import MovexBuilderRouteTransitionProvider from '@/contexts/movexBuilderRouteTransition/index.jsx';
import useMovexBuilderRouteTransition from '@/hooks/useMovexBuilderRouteTransition';
import { setupInterceptors } from '@/middlewares/api-interceptor';
import LoadingElement from '@/components/shared/loadingElement';
import PublicRoute from '@/middlewares/public-middle-ware';
import ProtectedRoute from '@/middlewares/protected-routes-middle-ware';
import ProtectedLayout from '@/components/protectedLayout';
import MediaPipeBootstrap from '@/components/mediaPipeBootstrap';
import RealtimeAudioOutlet from '@inPerformance/components/realtime-audio-outlet';
import RealtimeDebugPanel from '@inPerformance/components/realtime-debug-panel';
import NetworkDiagnosticsPanel from '@inPerformance/components/network-diagnostics-panel';
import NetworkMeterPanel from '@inPerformance/components/network-meter-panel';
import { isNetworkMeterEnabled } from '@inPerformance/services/network-meter';
import { DEVICE_PERFORMANCE_ROUTE } from '@inPerformance/services/device-performance';
import { disconnectRealtimeConnection } from '@inPerformance/services/realtime-connection-store';
import { FEATURES } from '@config/features';
import 'twin.macro';

// Routes the voice agent stays alive across. /camera-permission, /try-demo and
// /exercise are one continuous session and the user moves between them, so
// tearing the call down on any of them would drop the agent mid-session.
// Anything else ends it.
//
// /camera-permission is where the call is now established (see
// features/camera-permission) — leaving it out here would disconnect the call
// on the very page that opens it.
const REALTIME_ROUTES_ALIVE = ['/camera-permission', '/try-demo', '/exercise'];

const RouteLoadingScreen = () => (
  <div tw="absolute inset-0 z-50 flex items-center justify-center bg-white">
    <span tw="text-sm font-semibold text-text_secondary">Loading page</span>
  </div>
);

const AppRoutes = () => {
  const { publicRoute, privateRoute, guestRoute } = useRoutes();
  const { isNavigatingToMovexBuilder } = useMovexBuilderRouteTransition();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setupInterceptors(navigate);
  }, [navigate]);

  // Disconnection is owned here, by the router — not by a cleanup inside the
  // exercise page. A page-level cleanup fires on every internal navigation and
  // would kill the call the moment the user moves between the demo and the
  // exercise.
  useEffect(() => {
    if (!REALTIME_ROUTES_ALIVE.includes(pathname)) {
      disconnectRealtimeConnection();
    }
  }, [pathname]);

  return (
    <div tw="relative">
      {isNavigatingToMovexBuilder && <RouteLoadingScreen />}
      <Suspense
        fallback={
          <div tw="flex items-center justify-center min-h-screen">
            <LoadingElement width={80} height={80} />
          </div>
        }
      >
        <Routes>
          <Route element={<PublicRoute />}>
            {publicRoute.map(route => (
              <Route path={route.route} element={route.element} key={route.route} />
            ))}
          </Route>
          {/* Guest routes — the patient exercise flow. They still get the app
              chrome, but neither ProtectedRoute nor PublicRoute can bounce a
              visitor off them: there's no clinician login involved. */}
          <Route element={<ProtectedLayout />}>
            {guestRoute.map(item => (
              <Route element={item.element} key={item.route} path={item.route} />
            ))}
          </Route>
          <Route element={<ProtectedRoute />}>
            <Route element={<ProtectedLayout />}>
              <Route index element={<Navigate to="/home" replace />} />
              {privateRoute.map(route => (
                <Route
                  key={route.route}
                  path={route.route}
                  element={
                    route.requiredPermission || route.deniedUserTypes ? (
                      <PermissionRouteGuard
                        requiredPermission={route.requiredPermission}
                        deniedUserTypes={route.deniedUserTypes}
                      >
                        {route.element}
                      </PermissionRouteGuard>
                    ) : (
                      route.element
                    )
                  }
                >
                  {route.children &&
                    route.children.map(child => (
                      <Route key={child.route} path={child.route} element={child.element} />
                    ))}
                </Route>
              ))}
              {/* An unknown path also resolves to /home — through this guard,
                  so a signed-out visitor lands on /login instead. */}
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

// Global developer panels. Kept off the device-performance diagnostic: they sit
// fixed over its controls, and their own re-renders would be counted in the
// render/main-thread numbers it is measuring.
const DeveloperPanels = () => {
  const { pathname } = useLocation();
  if (pathname === DEVICE_PERFORMANCE_ROUTE) {
    return null;
  }
  return (
    <>
      {/* Diagnostic tool — see the component for the three ways to
          hide/re-show it wherever the flag is on. */}
      {FEATURES.AI_DEBUG_PANEL && <RealtimeDebugPanel />}
      {/* Network connectivity investigation, Step 3 — temporary developer
          diagnostics, own flag so it can ship without RealtimeDebugPanel.
          Toggle with Ctrl+Alt+N. */}
      {FEATURES.NETWORK_DIAGNOSTICS && <NetworkDiagnosticsPanel />}
      {/* Development-only network usage meter. Toggle with Ctrl+Alt+M. */}
      {isNetworkMeterEnabled() && <NetworkMeterPanel />}
    </>
  );
};

function App() {
  // Define fade transition
  const Fade = cssTransition({
    enter: 'fadeIn', // Uses CSS class `.fadeIn`
    exit: 'fadeOut', // Uses CSS class `.fadeOut`
    duration: [3, 3], // [enter duration, exit duration]
  });

  return (
    <AppErrorBoundary>
      <BrowserRouter>
        <MovexBuilderRouteTransitionProvider>
          <MediaPipeBootstrap />
          {/* Mounted once for the app's lifetime — see the component for why it
              can't live inside the exercise page's AudioSection. */}
          <RealtimeAudioOutlet />
          <DeveloperPanels />
          <div tw="w-screen h-[100dvh]">
            <AppRoutes />
          </div>
        </MovexBuilderRouteTransitionProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

export default App;
